import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { PersistentStorageManager } from '../../../../models/Storage';
import { invalidateEmailTemplateLists } from '../../../../queries/Templates/refreshSavedTemplateQueries';
import { useUpdateTemplateMutation } from '../../../../queries/Templates/useUpdateTemplateMutation';
import { Checkbox } from '../../../components/Checkbox';
import { styleSheet } from './styles';
import { serializeContent } from './unsavedChanges';

const AutoSaveStorageKey = 'emailAutoSaveEnabled';

function formatTimestamp(date: Date): string {
	return (
		'Saved at ' +
		date.toLocaleTimeString(undefined, {
			hour: 'numeric',
			minute: '2-digit',
		})
	);
}

interface IEmailAutoSaveProps {
	/** Template associated with the email body — must have an id for auto-save to function */
	emailBodyTemplate: Api.ITemplate | undefined;
	/** Current editor content state */
	emailBodyEditorState: Api.IRichContentEditorState | undefined;
	/** Current subject text */
	subject: string | undefined;
	/** Master toggle — false when readonly, automation mode, custom email, or not saveable */
	enabled: boolean;
	/** Optional impersonation context for admin impersonation */
	impersonationContext?: Api.IImpersonationContext;
	/** Called when autosave is toggled on or off */
	onAutoSaveToggled?: (enabled: boolean) => void;
	/** Called after each successful save with the content and subject that were saved */
	onSaved?: (content: Api.IRawRichTextContentState | null, subject: string | null) => void;
	/** Debounce delay in milliseconds (default: 2500) */
	debounceMs?: number;
	/** Optional CSS class for the wrapper */
	className?: string;
}

/**
 * Provides an "Autosave" checkbox with debounced auto-save for email templates.
 *
 * Saves via PATCH /template/{id} when the template has an id (user/account template).
 *
 * The parent controls visibility via `enabled` — autosave only appears when a direct
 * save is possible (same guard as the "Save" button). System/industry templates that
 * require "Save As" do not show the autosave checkbox.
 */
export function EmailAutoSave({
	emailBodyTemplate,
	emailBodyEditorState,
	subject,
	enabled,
	impersonationContext,
	onAutoSaveToggled,
	onSaved,
	debounceMs = 2500,
	className,
}: IEmailAutoSaveProps) {
	const [autoSaveEnabled, setAutoSaveEnabled] = useState(
		() => PersistentStorageManager.local.get(AutoSaveStorageKey) === 'true'
	);
	const [savedLabel, setSavedLabel] = useState<string | null>(null);
	const lastSavedAtRef = useRef<Date | null>(null);

	const mountedRef = useRef(true);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	// Baselines for change detection — initialized on first active render, updated after each save
	const lastSavedContentRef = useRef<string | null>(null);
	const lastSavedSubjectRef = useRef<string | null>(null);
	const isInitializedRef = useRef(false);

	const templateId = emailBodyTemplate?.id;
	const isActive = enabled && autoSaveEnabled && !!templateId;

	const { logApiError } = useEventLogging('EmailAutoSave');
	const hasSavedRef = useRef(false);
	// Refreshing every card list on each debounced save would refetch them while the user types; do it once on unmount
	const templateMutation = useUpdateTemplateMutation({
		invalidateTemplateLists: false,
		onSuccess: () => {
			hasSavedRef.current = true;
			// A save that lands after close missed the refresh on unmount
			if (!mountedRef.current) {
				invalidateEmailTemplateLists();
			}
		},
		retry: 3,
		templateId,
	});

	// Transition "Saved just now" → timestamp after 60s
	useEffect(() => {
		if (savedLabel !== 'Saved just now' || !lastSavedAtRef.current) {
			return;
		}
		const id = setTimeout(() => {
			if (mountedRef.current && lastSavedAtRef.current) {
				setSavedLabel(formatTimestamp(lastSavedAtRef.current));
			}
		}, 60_000);
		return () => clearTimeout(id);
	}, [savedLabel]);

	// Notify parent whether autosave is effectively active (both enabled and checked)
	const effectivelyActive = enabled && autoSaveEnabled;
	useEffect(() => {
		onAutoSaveToggled?.(effectivelyActive);
	}, [effectivelyActive, onAutoSaveToggled]);

	const currentContent = serializeContent(emailBodyEditorState);
	const currentSubject = subject ?? null;

	const performSave = (contentToSave: string | null, subjectToSave: string | null) => {
		const contentChanged = contentToSave !== lastSavedContentRef.current;
		const subjectChanged = subjectToSave !== lastSavedSubjectRef.current;
		if (!emailBodyTemplate?.id || (!contentChanged && !subjectChanged)) {
			return;
		}

		const updatedTemplate: Api.ITemplate = {
			...emailBodyTemplate,
			subject: subjectToSave ?? emailBodyTemplate.subject,
		};
		if (contentToSave !== null) {
			updatedTemplate.content = JSON.parse(contentToSave) as Api.IRawRichTextContentState;
		}

		// The scope sends saves in the order started, so a retry of older text cannot land after this one. These
		// callbacks fire only for the latest mutate(), and only while mounted.
		templateMutation.mutate(
			{ template: updatedTemplate, impersonationContext },
			{
				onError: error => {
					setSavedLabel('Save failed');
					logApiError('AutoSave-Error', Api.asApiError(error));
				},
				onSuccess: () => {
					lastSavedContentRef.current = contentToSave;
					lastSavedSubjectRef.current = subjectToSave;
					lastSavedAtRef.current = new Date();
					setSavedLabel('Saved just now');
					onSaved?.(
						contentToSave !== null ? (JSON.parse(contentToSave) as Api.IRawRichTextContentState) : null,
						subjectToSave
					);
				},
			}
		);
	};

	// Stable ref so the debounce effect doesn't re-run when performSave identity changes
	const performSaveRef = useRef(performSave);
	performSaveRef.current = performSave;

	// Unified save effect: handles initialization, debouncing, and reset
	useEffect(() => {
		if (!isActive) {
			isInitializedRef.current = false;
			return;
		}

		// First activation: save immediately to establish baselines
		if (!isInitializedRef.current) {
			isInitializedRef.current = true;
			performSaveRef.current(currentContent, currentSubject);
			return;
		}

		// Subsequent changes: debounce
		const contentChanged = currentContent !== lastSavedContentRef.current;
		const subjectChanged = currentSubject !== lastSavedSubjectRef.current;

		if (!contentChanged && !subjectChanged) {
			return;
		}

		debounceTimerRef.current = setTimeout(() => {
			if (mountedRef.current) {
				performSaveRef.current(currentContent, currentSubject);
			}
		}, debounceMs);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [isActive, currentContent, currentSubject, debounceMs]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			mountedRef.current = false;
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
			if (hasSavedRef.current) {
				invalidateEmailTemplateLists();
			}
		};
	}, []);

	if (!enabled) {
		return null;
	}

	const statusText = templateMutation.isPending ? 'Saving...' : savedLabel;

	return (
		<div className={className}>
			{statusText ? <span className={css(styleSheet.autoSaveStatusText)}>{statusText}</span> : null}
			<Checkbox
				checked={autoSaveEnabled}
				id='enable-plaintext-autosave'
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					const checked = e.target.checked;
					setAutoSaveEnabled(checked);
					PersistentStorageManager.local.set(AutoSaveStorageKey, String(checked));
				}}
			>
				<div className={css(styleSheet.autoSaveCheckboxText)}>Autosave</div>
			</Checkbox>
		</div>
	);
}
